import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { useSpeedSearch } from './useSpeedSearch';

/** One flattened, currently-visible outline row to render. */
export interface OutlineRow<TNode> {
  node: TNode;
  id: string;
  level: number;
  hasChildren: boolean;
  /** Whether this row's children are shown (chevron state). */
  open: boolean;
  isMatch: boolean;
  isFocused: boolean;
  /** Matched `[start,end)` in the label while searching, else null. */
  range: [number, number] | null;
}

export interface UseOutlineSpeedSearchOptions<TNode> {
  roots: readonly TNode[];
  getId: (node: TNode) => string;
  getLabel: (node: TNode) => string;
  getChildren: (node: TNode) => readonly TNode[];
  /** Jump the document to a row (Enter / click). */
  onActivate: (node: TNode) => void;
  /** The scrollable body element, for keeping the focused match in view. */
  bodyRef: RefObject<HTMLElement | null>;
}

export interface UseOutlineSpeedSearch<TNode> {
  query: string;
  active: boolean;
  matchCount: number;
  /** Flattened visible rows, in document order. */
  rows: OutlineRow<TNode>[];
  /** Toggle a row's collapse state (chevron click). */
  toggleCollapse: (id: string) => void;
  /** Attach to the panel body's `onKeyDown`. Speed search first, then Enter/Space
   *  jump. Lives on the container (not per-row) so typing works whenever the
   *  panel has focus, and so it never needs to move DOM focus onto a row. */
  handleKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  /** Attach to the panel body's `onBlur` — clears the query on leaving the pane. */
  onBlur: (e: React.FocusEvent<HTMLElement>) => void;
}

interface FlatEntry<TNode> {
  node: TNode;
  id: string;
  label: string;
  level: number;
  hasChildren: boolean;
  /** Ids of this node's ancestors (for auto-expand). */
  ancestors: string[];
}

export function useOutlineSpeedSearch<TNode>({
  roots,
  getId,
  getLabel,
  getChildren,
  onActivate,
  bodyRef,
}: UseOutlineSpeedSearchOptions<TNode>): UseOutlineSpeedSearch<TNode> {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // Full document-order flattening of EVERY node (ignores collapse) — this is
  // what we match against and what lets us auto-expand to reveal matches.
  const flat = useMemo<FlatEntry<TNode>[]>(() => {
    const out: FlatEntry<TNode>[] = [];
    const walk = (nodes: readonly TNode[], level: number, ancestors: string[]): void => {
      for (const node of nodes) {
        const id = getId(node);
        const children = getChildren(node);
        out.push({ node, id, label: getLabel(node), level, hasChildren: children.length > 0, ancestors });
        if (children.length > 0) walk(children, level + 1, [...ancestors, id]);
      }
    };
    walk(roots, 0, []);
    return out;
  }, [roots, getId, getLabel, getChildren]);

  const speed = useSpeedSearch<FlatEntry<TNode>>({
    items: flat,
    getId: (f) => f.id,
    getLabel: (f) => f.label,
    focusedId,
    onJumpToMatch: setFocusedId,
  });

  const nodeById = useMemo(() => {
    const m = new Map<string, TNode>();
    for (const f of flat) m.set(f.id, f.node);
    return m;
  }, [flat]);

  // While searching, force-open the ancestors of every match so matches render.
  const forceOpen = useMemo<Set<string>>(() => {
    if (!speed.active) return new Set();
    const open = new Set<string>();
    const byId = new Map(flat.map((f) => [f.id, f] as const));
    for (const id of speed.matches) {
      const entry = byId.get(id);
      if (entry) for (const a of entry.ancestors) open.add(a);
    }
    return open;
  }, [speed.active, speed.matches, flat]);

  const isOpen = useCallback(
    (id: string): boolean => !collapsed.has(id) || forceOpen.has(id),
    [collapsed, forceOpen],
  );

  // Visible render list: skip the subtree of a closed node.
  const rows = useMemo<OutlineRow<TNode>[]>(() => {
    const out: OutlineRow<TNode>[] = [];
    const walk = (nodes: readonly TNode[], level: number): void => {
      for (const node of nodes) {
        const id = getId(node);
        const children = getChildren(node);
        const hasChildren = children.length > 0;
        const open = isOpen(id);
        const isMatch = speed.matchSet.has(id);
        out.push({
          node,
          id,
          level,
          hasChildren,
          open,
          isMatch,
          isFocused: id === focusedId,
          range: speed.active && isMatch ? speed.rangeFor(getLabel(node)) : null,
        });
        if (hasChildren && open) walk(children, level + 1);
      }
    };
    walk(roots, 0);
    return out;
  }, [roots, getId, getChildren, getLabel, isOpen, speed, focusedId]);

  const toggleCollapse = useCallback((id: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>): void => {
      const r = speed.handleKey(e);
      if (r.handled) {
        if (r.focusId) setFocusedId(r.focusId);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        // While searching, Enter/Space activates the FOCUSED match. Otherwise it
        // activates the row the key was fired on (keyboard-activating a focused
        // row), found from the event target — we never rely on DOM focus living
        // on a row, so the panel can't hijack the editor's focus.
        let target: TNode | undefined;
        if (speed.active && focusedId) target = nodeById.get(focusedId);
        if (target === undefined) {
          const el = (e.target as HTMLElement | null)?.closest?.('[data-outline-id]') ?? null;
          const id = el?.getAttribute('data-outline-id') ?? undefined;
          if (id !== undefined) target = nodeById.get(id);
        }
        if (target !== undefined) {
          e.preventDefault();
          onActivate(target);
        }
      }
    },
    [speed, onActivate, focusedId, nodeById],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>): void => {
      if (!speed.active) return;
      const next = e.relatedTarget as Node | null;
      if (next && e.currentTarget.contains(next)) return; // moving within the pane
      speed.reset();
      setFocusedId(null);
    },
    [speed],
  );

  // When the search ends (Esc / idle / backspace-to-empty), drop the focused
  // match so its highlight clears and the scroll effect below goes idle.
  useEffect(() => {
    if (!speed.active) setFocusedId(null);
  }, [speed.active]);

  // Keep the focused match scrolled into view as you type / cycle. We do NOT move
  // DOM focus here: doing so hijacked focus from the editor on every re-render
  // (the old effect re-ran whenever `rows` changed). Enter activates the focused
  // match via `focusedId` regardless of which element holds DOM focus.
  useEffect(() => {
    if (focusedId === null) return;
    const body = bodyRef.current;
    if (!body) return;
    const row = body.querySelector<HTMLElement>(`[data-outline-id="${CSS.escape(focusedId)}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [focusedId, bodyRef]);

  return {
    query: speed.query,
    active: speed.active,
    matchCount: speed.matches.length,
    rows,
    toggleCollapse,
    handleKeyDown,
    onBlur,
  };
}
